import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getBgPalette,
  getBgPattern,
  setBgPalette,
  setBgPattern,
  type BgPalette,
  type BgPattern,
} from '@/stores/preferences';

export function BgPreferencesSetting() {
  const { t } = useTranslation();
  const [palette, setPaletteState] = useState<BgPalette>(() => getBgPalette());
  const [pattern, setPatternState] = useState<BgPattern>(() => getBgPattern());

  const palettes: Array<{ value: BgPalette; color: string; accent: string }> = [
    { value: 'purple', color: 'var(--c-purple)', accent: 'var(--c-mint)' },
    { value: 'mint', color: 'var(--c-mint)', accent: 'var(--c-cyan)' },
    { value: 'cyan', color: 'var(--c-cyan)', accent: 'var(--c-purple)' },
    { value: 'rose', color: 'var(--c-rose)', accent: 'var(--c-amber)' },
    { value: 'amber', color: 'var(--c-amber)', accent: 'var(--c-purple)' },
    { value: 'ocean', color: 'var(--c-blue)', accent: 'var(--c-mint)' },
  ];

  const patterns: BgPattern[] = [
    'dots',
    'grid',
    'graph',
    'crosshatch',
    'diagonal',
    'none',
  ];

  const pickPalette = (v: BgPalette) => {
    setPaletteState(v);
    setBgPalette(v);
  };
  const pickPattern = (v: BgPattern) => {
    setPatternState(v);
    setBgPattern(v);
  };

  return (
    <div className="space-y-4">
      {/* Paletă — swatch buttons */}
      <div role="group" aria-labelledby="settings-palette-label">
        <div id="settings-palette-label" className="block text-xs font-medium text-[var(--text-muted)] mb-2">
          {t('admin_ui.settings.palette_label')}
        </div>
        <div className="flex flex-wrap gap-2">
          {palettes.map((p) => {
            const active = palette === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => pickPalette(p.value)}
                className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors"
                style={{
                  background: active ? 'var(--accent-bg)' : 'var(--bg-surface)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  color: 'var(--text)',
                  boxShadow: active ? 'var(--glow-mint)' : undefined,
                }}
                aria-pressed={active}
              >
                <span className="flex -space-x-1">
                  <span
                    className="w-4 h-4 rounded-full"
                    style={{ background: p.color, border: '1px solid var(--border-strong)' }}
                  />
                  <span
                    className="w-4 h-4 rounded-full"
                    style={{ background: p.accent, border: '1px solid var(--border-strong)' }}
                  />
                </span>
                {t(`admin_ui.colors.${p.value}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Pattern */}
      <div role="group" aria-labelledby="settings-pattern-label">
        <div id="settings-pattern-label" className="block text-xs font-medium text-[var(--text-muted)] mb-2">
          {t('admin_ui.settings.pattern_label')}
        </div>
        <div className="flex flex-wrap gap-2">
          {patterns.map((pat) => {
            const active = pattern === pat;
            return (
              <button
                key={pat}
                type="button"
                onClick={() => pickPattern(pat)}
                className="inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors"
                style={{
                  background: active ? 'var(--accent-bg)' : 'var(--bg-surface)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  color: 'var(--text)',
                  boxShadow: active ? 'var(--glow-mint)' : undefined,
                }}
                aria-pressed={active}
              >
                <PatternSwatch kind={pat} />
                {t(`admin_ui.settings.pattern_${pat}`)}
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-[var(--text-muted)]">
        {t('admin_ui.settings.background_hint')}
      </p>
    </div>
  );
}

function PatternSwatch({ kind }: { kind: BgPattern }) {
  const base: React.CSSProperties = {
    width: 18,
    height: 18,
    borderRadius: 3,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-strong)',
    flexShrink: 0,
  };
  if (kind === 'dots') {
    return (
      <span
        style={{
          ...base,
          backgroundImage:
            'radial-gradient(circle at 1.5px 1.5px, var(--text-dim) 1px, transparent 1.5px)',
          backgroundSize: '6px 6px',
        }}
      />
    );
  }
  if (kind === 'grid') {
    return (
      <span
        style={{
          ...base,
          backgroundImage:
            'linear-gradient(var(--text-dim) 1px, transparent 1px), linear-gradient(90deg, var(--text-dim) 1px, transparent 1px)',
          backgroundSize: '6px 6px',
        }}
      />
    );
  }
  if (kind === 'diagonal') {
    return (
      <span
        style={{
          ...base,
          backgroundImage:
            'repeating-linear-gradient(45deg, var(--text-dim) 0, var(--text-dim) 1px, transparent 1px, transparent 5px)',
        }}
      />
    );
  }
  if (kind === 'graph') {
    return (
      <span
        style={{
          ...base,
          backgroundImage:
            'linear-gradient(var(--text-dim) 1px, transparent 1px), linear-gradient(90deg, var(--text-dim) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '3px 3px, 3px 3px, 15px 15px, 15px 15px',
        }}
      />
    );
  }
  if (kind === 'crosshatch') {
    return (
      <span
        style={{
          ...base,
          backgroundImage:
            'repeating-linear-gradient(45deg, var(--text-dim) 0, var(--text-dim) 1px, transparent 1px, transparent 5px), repeating-linear-gradient(-45deg, var(--text-dim) 0, var(--text-dim) 1px, transparent 1px, transparent 5px)',
        }}
      />
    );
  }
  return <span style={base} />;
}
